// @ts-check

import { analyticsRepository, cmsRepository } from "./api.repository";

// ─── 기본값 ─────────────────────────────────────────────────────────────────
const DEFAULT_LANGUAGE = "KR";

// ─── 언어 변환 ───────────────────────────────────────────────────────────────
//
// toCmsLang  : "KR"/"EN" → cms 언어 코드 "ko"/"en"
// toLegacyLang : cms 코드 → 컴포넌트가 기대하는 대문자 코드 "KR"/"EN"
//
const toCmsLang = (language) =>
  cmsRepository.getCmsLanguage(language || DEFAULT_LANGUAGE) || "ko";

const toLegacyLang = (language) =>
  toCmsLang(language) === "en" ? "EN" : "KR";

// ─── 공통 유틸 ───────────────────────────────────────────────────────────────

const getId = (item) => item?.id || item?._id || "";

const getDate = (item) =>
  item?.displayDate || item?.date || item?.createdAt || item?.updatedAt || "";

const isImageFile = (attachment) => {
  const mime = attachment?.mimeType || "";
  const name = attachment?.fileName || attachment?.fileUrl || "";
  return mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(name);
};

// ─── 번역 선택 ───────────────────────────────────────────────────────────────
//
// BE는 translations[].language.code 를 leftJoinAndSelect 로 함께 반환한다.
// → 언어 UUID 없이 code 문자열 비교로 번역을 선택한다.
//
// 우선순위: ① 언어 코드 일치 → ② 언어 중립(language 필드 없음) → ③ 첫 번째 항목(폴백)
//

const getEntryLangCode = (entry) =>
  cmsRepository.getCmsLanguage(
    entry?.language?.code || entry?.languageCode || entry?.langCode || entry?.code
  ) || "";

const isLanguageNeutral = (entry) =>
  !entry?.language &&
  !entry?.languageId &&
  !entry?.languageCode &&
  !entry?.langCode &&
  !entry?.code;

/**
 * entries 배열에서 현재 언어에 맞는 항목을 선택한다.
 */
const pickByLanguage = (entries, language) => {
  const list = Array.isArray(entries) ? entries.filter(Boolean) : [];
  if (!list.length) return null;
  const langCode = toCmsLang(language);
  return (
    list.find((e) => getEntryLangCode(e) === langCode) ||
    list.find(isLanguageNeutral) ||
    list[0]
  );
};

/**
 * 항목의 translations 배열에서 현재 언어 번역을 꺼낸다.
 * translations 가 없으면 item 자체를 번역 데이터로 사용한다.
 */
const pickTranslation = (item, language) =>
  pickByLanguage(item?.translations, language) || item?.translation || item || {};

// ─── 첨부파일 언어 보충 ─────────────────────────────────────────────────────
//
// BE IR·전자공고 attachments 에는 langCode 필드가 없다.
// CMS admin 저장 시 ensureFileNameForLang 으로 파일명에 _ko / _en suffix 를 붙이므로
// 파일명 suffix 를 감지해 langCode 를 보충한다.
// 이미 언어 필드(langCode / language / languageCode)가 있으면 그대로 둔다.
//

/** CMS 에서 사용하는 언어 코드 목록 (파일명 suffix 감지용) */
const ATTACHMENT_LANG_CODES = ["ko", "en"];

/**
 * 파일명에서 언어 코드 추론.
 * "report_ko.pdf" → "ko", "doc-en.pdf" → "en", 없으면 null.
 */
const resolveLangCodeFromFileName = (fileName) => {
  const base = (fileName || "").toLowerCase();
  for (const code of ATTACHMENT_LANG_CODES) {
    if (
      base.includes(`_${code}.`) ||
      base.includes(`-${code}.`) ||
      base.endsWith(`_${code}`) ||
      base.endsWith(`-${code}`)
    ) {
      return code;
    }
  }
  return null;
};

/**
 * 언어 필드가 없는 첨부파일에 파일명 suffix 기반으로 langCode 를 보충한다.
 * pickByLanguage 가 올바른 언어 항목을 선택할 수 있도록 한다.
 * 이미 언어 필드가 있거나 suffix 가 없는 항목은 그대로 반환한다.
 */
const enrichAttachmentsWithLangCode = (attachments) => {
  if (!Array.isArray(attachments)) return attachments;
  return attachments.map((a) => {
    if (!a) return a;
    // languageId(UUID)는 getEntryLangCode 가 해석하지 못하므로 guard 에서 제외한다.
    // languageId 만 있는 항목도 파일명 suffix 기반 enrichment 를 시도하도록 한다.
    if (a.language || a.languageCode || a.langCode || a.code) return a;
    const langCode = resolveLangCodeFromFileName(a.fileName || a.fileUrl || "");
    if (!langCode) return a;
    return { ...a, langCode };
  });
};

// ─── 첨부파일 선택 ──────────────────────────────────────────────────────────

const getAttachmentUrl = (attachment) =>
  cmsRepository.resolveFileUrl(
    attachment?.fileUrl ||
      attachment?.url ||
      attachment?.filePath ||
      attachment?.path ||
      ""
  );

const getAttachmentFileName = (attachment) =>
  attachment?.fileName || attachment?.originFileName || attachment?.name || "";

/**
 * 첨부파일 목록에서 type / displayType / imageOnly 조건으로 필터 후 언어 선택.
 */
const pickAttachment = (attachments, language, options = {}) => {
  const list = Array.isArray(attachments) ? attachments.filter(Boolean) : [];
  const filtered = list.filter((a) => {
    if (options.type && a.type && a.type !== options.type) return false;
    if (options.displayType && a.displayType && a.displayType !== options.displayType)
      return false;
    if (options.imageOnly && !isImageFile(a)) return false;
    return true;
  });
  return pickByLanguage(filtered.length ? filtered : list, language);
};

// ─── 페이지네이션 ────────────────────────────────────────────────────────────

/**
 * cms-backend 응답(page/pageNo, total/totalCount, ...)의 다양한 필드명을 정규화한다.
 */
const getPageInfo = (response) => {
  const items = Array.isArray(response?.items) ? response.items : [];
  const page = Number(response?.page || response?.pageNo || 1);
  const limit = Number(response?.limit || response?.pageSize || items.length || 1);
  const total = Number(response?.total || response?.totalCount || items.length);
  const totalPages = Number(response?.totalPages || Math.ceil(total / limit) || 1);
  return {
    items,
    page,
    limit,
    total,
    totalPages,
    hasMore: response?.hasMore ?? page < totalPages,
  };
};

/**
 * 컴포넌트가 기대하는 레거시 페이지 응답 형태로 변환한다.
 * (totalCount, totalPage, pageNo, isFirst, isLast, items, language)
 */
const buildPageResponse = (response, items, language) => {
  const p = getPageInfo(response);
  return {
    pageSize: p.limit,
    totalCount: p.total,
    totalPage: p.totalPages,
    pageNo: p.page,
    isLast: p.page >= p.totalPages,
    isFirst: p.page <= 1,
    items,
    language: toLegacyLang(language),
  };
};


/** YouTube URL → videoId 추출. shorts/embed/live 링크도 지원한다. */
const extractYouTubeId = (url = "") => {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      return u.pathname.split("/").filter(Boolean)[0] || "";
    }

    if (host.endsWith("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return v;

      const pathParts = u.pathname.split("/").filter(Boolean);
      const markerIdx = pathParts.findIndex((part) =>
        ["shorts", "embed", "live", "v"].includes(part)
      );
      if (markerIdx >= 0 && pathParts[markerIdx + 1]) {
        return pathParts[markerIdx + 1];
      }
    }
  } catch {
    // 이미 videoId 이거나 URL이 아닌 값
  }
  return String(url).trim();
};

// ─── 도메인별 데이터 매핑 ────────────────────────────────────────────────────

const mapNewsItem = (item, language) => {
  if (!item) return null;
  const t = pickTranslation(item, language);
  const allAttachments = item.attachments || item.thumbnails || item.files || [];
  const thumbnail =
    pickAttachment(item.thumbnails, language, { imageOnly: true }) ||
    pickAttachment(allAttachments, language, { imageOnly: true }) ||
    pickAttachment(allAttachments, language);
  const contentAttachment =
    pickAttachment(item.files, language, { imageOnly: true }) ||
    pickAttachment(allAttachments, language, { imageOnly: true }) ||
    thumbnail;
  // 번역별 thumbnailUrl 을 우선 사용 — 언어 전환 시 올바른 썸네일 표시
  const fileUrl =
    (t.thumbnailUrl ? cmsRepository.resolveFileUrl(t.thumbnailUrl) : "") ||
    getAttachmentUrl(thumbnail) ||
    cmsRepository.resolveFileUrl(item.thumbnailUrl || "");

  return {
    id: getId(item),
    _id: getId(item),
    title: t.title || item.title || "",
    date: getDate(item),
    createdAt: item.createdAt || getDate(item),
    updatedAt: item.updatedAt || "",
    link: t.externalLink || item.externalLink || item.url || item.link || "",
    exposure: item.isPublic,
    serverFileName: fileUrl,
    originFileName: getAttachmentFileName(thumbnail),
    fileUrl,
    category: item.categoryName || item.category?.name || "",
    content: getAttachmentUrl(contentAttachment) || t.bodyImageUrl || t.description || item.content || "",
    language: toLegacyLang(language),
    order: item.order,
  };
};

const mapPressReleaseItem = (item, language) => {
  if (!item) return null;
  const t = pickTranslation(item, language);
  // BE가 내려주는 per-language 썸네일 배열 (langCode 포함).
  // item.attachments 는 일반 첨부파일(썸네일 제외)이므로 구분해서 사용한다.
  const thumbnailList = Array.isArray(item.thumbnails) ? item.thumbnails : [];
  const thumbnail =
    pickAttachment(thumbnailList, language, { imageOnly: true }) ||
    pickAttachment(thumbnailList, language);
  const fileUrl =
    getAttachmentUrl(thumbnail) ||
    cmsRepository.resolveFileUrl(item.thumbnailUrl || t.thumbnailUrl || "");

  return {
    id: getId(item),
    _id: getId(item),
    title: t.title || item.title || "",
    date: getDate(item),
    createdAt: item.createdAt || getDate(item),
    updatedAt: item.updatedAt || "",
    mediaCompany: item.newsAgency || item.newsCompany || "",
    link: t.externalLink || item.externalLink || item.url || item.link || "",
    exposure: item.isPublic,
    serverFileName: fileUrl,
    originFileName: getAttachmentFileName(thumbnail),
    fileUrl,
    language: toLegacyLang(language),
    order: item.order,
  };
};

const mapDocumentItem = (item, language) => {
  if (!item) return null;
  const t = pickTranslation(item, language);
  // IR·전자공고 BE 응답의 attachments 에는 langCode 가 없다.
  // 파일명 suffix(_ko / _en)로 언어를 보충해 pickAttachment 가 올바른 항목을 선택하도록 한다.
  const attachments = enrichAttachmentsWithLangCode(item.attachments || item.files || []);
  const file = pickAttachment(attachments, language) || attachments[0];
  const fileUrl = getAttachmentUrl(file);

  return {
    id: getId(item),
    _id: getId(item),
    title: t.title || item.title || "",
    content: t.description || item.description || item.content || "",
    createdAt: getDate(item),
    date: getDate(item),
    fileUrl,
    serverFileName: fileUrl,
    originFileName: getAttachmentFileName(file),
    category: item.categoryName || item.category?.name || "",
    order: item.order,
  };
};

const mapVideoItem = (item, language) => {
  const t = pickTranslation(item, language);

  // BE videoSources 의 languageId 는 UUID 이므로 getEntryLangCode 가 해석하지 못한다.
  // translations 배열에서 languageId(UUID) → langCode 매핑 테이블을 구성해 보충한다.
  const langIdToCode = {};
  if (Array.isArray(item?.translations)) {
    for (const tr of item.translations) {
      if (tr?.language?.id && tr?.language?.code) {
        langIdToCode[tr.language.id] = tr.language.code;
      }
    }
  }
  const videoSources = (item?.videoSources || []).map((vs) => {
    if (!vs) return vs;
    // 이미 언어 코드 필드가 있으면 그대로 사용
    if (vs.langCode || vs.languageCode || vs.language) return vs;
    // languageId(UUID)가 있으면 langCode 로 변환해 보충
    if (vs.languageId) {
      const code = langIdToCode[vs.languageId];
      if (code) return { ...vs, langCode: code };
    }
    return vs;
  });

  const source = pickByLanguage(videoSources, language) || {};
  const videoId = extractYouTubeId(source.url || item?.link || "");

  // YouTube 영상 ID가 있으면 YouTube 제공 썸네일을 직접 사용
  // (CMS 업로드 썸네일은 없을 수 있어 엑박 발생 → YouTube hqdefault 는 항상 존재)
  const cmsThumb =
    source.thumbnailUrl ||
    pickAttachment(item?.attachments, language, { imageOnly: true })?.fileUrl ||
    "";
  const imageUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : cmsRepository.resolveFileUrl(cmsThumb);

  const categoryName = item?.categoryName || item?.category?.name || "LUMIR";

  return {
    id: getId(item),
    link: videoId,
    imageUrl,
    videoCategory: categoryName === "기본" ? "LUMIR" : categoryName,
    title: t.title || source.title || item?.title || "",
    createdAt: getDate(item),
  };
};

const mapNoticeItem = (item, language) => {
  const t = pickTranslation(item, language);
  const attachments = item?.attachments || [];
  const mobileImage = pickAttachment(attachments, language, {
    displayType: "mobile",
    imageOnly: true,
  });
  const pcImage = pickAttachment(attachments, language, {
    displayType: "pc",
    imageOnly: true,
  });
  const fallbackImage = pickAttachment(attachments, language, { imageOnly: true });

  // 공개 API는 attachments[] 대신 pcImageUrl/mobileImageUrl 플랫 필드로 반환 — 폴백 처리
  const mobileUrl =
    getAttachmentUrl(mobileImage || fallbackImage) ||
    cmsRepository.resolveFileUrl(item?.mobileImageUrl || "");
  const pcUrl =
    getAttachmentUrl(pcImage || fallbackImage) ||
    cmsRepository.resolveFileUrl(item?.pcImageUrl || "");

  return {
    alt: t.title || item?.title || "",
    link: t.externalLink || item?.externalLink || "",
    image: {
      mobile: mobileUrl,
      pc: pcUrl,
    },
  };
};

const mapBrochureItem = (item, language) => {
  if (!item) return null;
  const t = pickTranslation(item, language);
  // 브로슈어 BE 응답의 attachments 에는 langCode 가 없을 수 있다.
  // 파일명 suffix(_ko / _en)로 언어를 보충해 pickAttachment 가 올바른 항목을 선택하도록 한다.
  const attachments = enrichAttachmentsWithLangCode(item.attachments || item.files || []);
  const imageAttachment = pickAttachment(attachments, language, { imageOnly: true });
  const imageUrl =
    getAttachmentUrl(imageAttachment) ||
    cmsRepository.resolveFileUrl(item.thumbnailUrl || t.thumbnailUrl || "");
  const nonImageAttachments = attachments.filter((a) => !isImageFile(a));
  const file = pickAttachment(nonImageAttachments.length ? nonImageAttachments : attachments, language);
  const fileUrl = getAttachmentUrl(file);

  return {
    id: getId(item),
    title: t.title || item.title || "",
    createdAt: getDate(item),
    date: getDate(item),
    imageUrl,
    serverFileName: fileUrl,
    originFileName: getAttachmentFileName(file),
    fileUrl,
    isPublic: item.isPublic,
    order: item.order,
    language: toLegacyLang(language),
  };
};

// ─── 공개 서비스 API ─────────────────────────────────────────────────────────

export const cmsService = {
  // ── 뉴스 (루미르 스토리) ──────────────────────────────────────────────────

  async getNews(page, limit, language = DEFAULT_LANGUAGE, keyword) {
    const res = await cmsRepository.getNews(page, limit, language, { keyword });
    const p = getPageInfo(res);
    return buildPageResponse(res, p.items.map((item) => mapNewsItem(item, language)), language);
  },

  async getNewsById(newsId, language = DEFAULT_LANGUAGE) {
    const res = await cmsRepository.getNewsById(newsId, language);
    if (res?.currentNews || res?.prevNews || res?.nextNews) {
      return {
        prev: mapNewsItem(res.prevNews, language),
        now: mapNewsItem(res.currentNews, language),
        next: mapNewsItem(res.nextNews, language),
      };
    }
    return { prev: null, now: mapNewsItem(res, language), next: null };
  },

  // ── 보도자료 ──────────────────────────────────────────────────────────────

  async getPressReleases(page, limit, pageNo, totalPages, language = DEFAULT_LANGUAGE, keyword) {
    const res = await cmsRepository.getPressReleases(page, limit, language, { keyword });
    const p = getPageInfo(res);
    return buildPageResponse(
      res,
      p.items.map((item) => mapPressReleaseItem(item, language)),
      language
    );
  },

  async getPressReleaseById(pressReleaseId, language = DEFAULT_LANGUAGE) {
    const res = await cmsRepository.getPressReleaseById(pressReleaseId, language);
    return mapPressReleaseItem(res, language);
  },

  // ── 전자공시 ──────────────────────────────────────────────────────────────

  async getDisclosures(page, limit, language = DEFAULT_LANGUAGE, filters = {}) {
    const res = await cmsRepository.getDisclosures(page, limit, language, filters);
    const p = getPageInfo(res);
    return {
      pageNo: p.page,
      totalCount: p.total,
      totalPage: p.totalPages,
      isFirst: p.page <= 1,
      isLast: p.page >= p.totalPages,
      items: p.items.map((item) => mapDocumentItem(item, language)),
    };
  },

  async getDisclosureById(disclosureId, language = DEFAULT_LANGUAGE) {
    const res = await cmsRepository.getDisclosureById(disclosureId, language);
    return mapDocumentItem(res, language);
  },

  // ── 비디오 갤러리 ─────────────────────────────────────────────────────────

  async getVideos(page, limit, language = DEFAULT_LANGUAGE) {
    const res = await cmsRepository.getVideos(page, limit, language);
    const p = getPageInfo(res);
    return {
      pageNo: p.page,
      pageSize: p.limit,
      items: p.items.map((item) => mapVideoItem(item, language)),
    };
  },

  async getVideoById(videoId, language = DEFAULT_LANGUAGE) {
    return cmsRepository.getVideoById(videoId, language);
  },

  // ── 브로슈어 ──────────────────────────────────────────────────────────────

  async getBrochures(page, limit, language = DEFAULT_LANGUAGE, keyword) {
    const res = await cmsRepository.getBrochures(page, limit, language, { keyword });
    const p = getPageInfo(res);
    return buildPageResponse(res, p.items.map((item) => mapBrochureItem(item, language)), language);
  },

  async getBrochureById(brochureId, language = DEFAULT_LANGUAGE) {
    return cmsRepository.getBrochureById(brochureId, language);
  },

  // ── 주주총회 ──────────────────────────────────────────────────────────────

  async getMeetings(page, limit, language = DEFAULT_LANGUAGE) {
    return cmsRepository.getMeetings(page, limit, language);
  },

  async getMeetingById(meetingId, language = DEFAULT_LANGUAGE) {
    return cmsRepository.getMeetingById(meetingId, language);
  },

  // ── IR 자료 ───────────────────────────────────────────────────────────────

  async getIRMaterials(page, limit, language = DEFAULT_LANGUAGE) {
    const res = await cmsRepository.getIRMaterials(page, limit, language);
    const p = getPageInfo(res);
    return {
      total: p.total,
      page: p.page,
      limit: p.limit,
      totalPages: p.totalPages,
      hasMore: p.hasMore,
      items: p.items.map((item) => mapDocumentItem(item, language)),
    };
  },

  async getIRMaterialById(irMaterialId, language = DEFAULT_LANGUAGE) {
    try {
      const res = await cmsRepository.getIRMaterialById(irMaterialId, language);
      const m = mapDocumentItem(res, language);
      return {
        title: m?.title || "",
        content: m?.content || "",
        createdAt: m?.createdAt || "",
        fileUrl: m?.fileUrl || "",
        fileName: m?.originFileName || m?.title || "",
        originFileName: m?.originFileName || "",
      };
    } catch (e) {
      console.error("IR 자료 상세 조회 실패:", e);
      return { title: "", content: "", fileUrl: "", fileName: "", createdAt: "", originFileName: "" };
    }
  },

  // ── 팝업 / 공지 ───────────────────────────────────────────────────────────

  async getNotices(page, limit, language = DEFAULT_LANGUAGE) {
    const res = await cmsRepository.getNotices(page, limit, language);
    const p = getPageInfo(res);
    const now = Date.now();
    const notices = p.items
      .filter((n) => {
        const releasedAt = n?.releasedAt ? new Date(n.releasedAt) : null;
        return (
          n?.isPublic !== false &&
          (!releasedAt || Number.isNaN(releasedAt.getTime()) || releasedAt.getTime() <= now)
        );
      })
      .map((n) => mapNoticeItem(n, language))
      .filter((n) => n.alt && (n.image.mobile || n.image.pc));

    return toLegacyLang(language) === "EN" ? { kr: [], en: notices } : { kr: notices, en: [] };
  },

  async getNoticeById(noticeId, language = DEFAULT_LANGUAGE) {
    return cmsRepository.getNoticeById(noticeId, language);
  },

  // ── 분석 ──────────────────────────────────────────────────────────────────

  async createPageView(params) {
    return analyticsRepository.createPageView(params);
  },

  async updatePageView(params, options = {}) {
    return analyticsRepository.updatePageView(params, options);
  },
};

// ─── 하위 호환 export ────────────────────────────────────────────────────────
// 기존 import { apiSDKService } from "./api.service" 유지
export const apiSDKService = cmsService;
